import React from "react";
import { Link } from "react-router-dom";
import { FaWordpress, FaBuilding, FaShoppingCart, FaServer } from "react-icons/fa";
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';

const services = [
  {
    title: "Wordpress Website Design",
    desc: "Build stunning and responsive websites using the world's most popular CMS. Perfect for blogs, small businesses, and content-driven platforms.",
    icon: <FaWordpress className="text-3xl text-[#ff9b00]" />,
    link: "/website-design/wordpress-website-development",
  },
  {
    title: "Corporate Website Design",
    desc: "Professional and scalable websites for enterprises that focus on brand impact, security, and business-driven design.",
    icon: <FaBuilding className="text-3xl text-[#ff9b00]" />,
    link: "/website-design/corporate-website-development",
  },
  {
    title: "eCommerce Website Design",
    desc: "Create powerful eCommerce stores using platforms like WooCommerce, Magento, or Shopify to drive online sales and streamline customer experience.",
    icon: <FaShoppingCart className="text-3xl text-[#ff9b00]" />,
    link: "/website-design/ecommerce-website-development",
  },
  {
    title: "Website Hosting and Support",
    desc: "End-to-end hosting and website support with regular backups, uptime monitoring, and quick fixes. Keep your site live and secure, always.",
    icon: <FaServer className="text-3xl text-[#ff9b00]" />,
    link: "/website-design/website-hosting-support",
  },
];

const title="Custom Website Design Services | AKHSoft";
const description = "Professional website design services tailored to your business needs. Mobile-friendly, fast, and SEO-optimized websites by AKHSoft.";
const keywords = "website design, web development, responsive websites, SEO-friendly design, custom web solutions, AKHSoft";
const path = typeof window !== "undefined" ? window.location.pathname : "";

const WebsiteDesignPage = () => {
  return (
    <>
   <SEO
  title={title}
  description={description}
  keywords={keywords}
  path={path}
/>
    <div className="w-full max-w-[1800px] mx-auto px-4 py-10">
      {/* Hero Section */}
      <section className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-bold text-[#ff9b00] mb-2">Custom Website Design Services for Businesses</h1>
        <h2 className="text-xl text-gray-700 mb-4">Responsive, SEO-Friendly, Conversion-Focused Web Design</h2>
        <p className="max-w-3xl mx-auto text-gray-700">
         Build trust and increase conversions with a modern website tailored to your audience. 
  Our expert team delivers responsive, fast-loading, and optimized websites that help your brand grow online.
        </p>
      </section>

      {/* Descriptive Section */}
      <section className="bg-[#fff4e5] rounded-xl p-6 md:p-10 mb-10 border border-[#ff9b00]/30">
        <h3 className="text-2xl font-semibold text-[#ff9b00] mb-4">Why Your Business Needs a Professional Website</h3>
        <p className="text-gray-700 mb-2">
          A website acts as your 24/7 sales representative. It’s often the first impression customers have of your brand.
        </p>
        <p className="text-gray-700 mb-2">
          Our designs go beyond appearance. We focus on user experience, SEO optimization, mobile responsiveness, and business alignment to ensure your site drives measurable results.
        </p>
        <p className="text-gray-700">
          Whether you're launching a new business or rebranding, we help you craft a site that not only looks good—but performs.
        </p>
      </section>

      {/* Services Grid */}
      <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-12">
        {services.map((service, idx) => (
          <div
            key={idx}
            className="bg-white border border-[#ff9b00]/20 rounded-xl shadow-sm hover:shadow-md transition p-6 flex flex-col items-center text-center"
          >
            {service.icon}
            <h4 className="mt-4 text-lg font-semibold text-[#0a3c6d]">{service.title}</h4>
            <p className="text-sm text-gray-600 mt-2">{service.desc}</p>
            <Link
              to={service.link}
              className="mt-4 text-sm text-[#ff9b00] hover:underline font-medium"
            >
              Read More →
            </Link>
          </div>
        ))}
      </section>

      {/* Our Process */}
      <section className="bg-[#fff8f0] rounded-xl p-6 md:p-10 mb-12 border border-[#ff9b00]/40">
        <h3 className="text-2xl font-semibold text-[#ff9b00] mb-4">Our Website Design Approach</h3>
        <ul className="list-disc pl-6 text-gray-700 space-y-2">
          <li><strong>Discovery & Planning:</strong> We understand your goals, competitors, and audience.</li>
          <li><strong>Design & Wireframes:</strong> We create modern, mobile-first designs tailored to your brand.</li>
          <li><strong>Development:</strong> Clean, scalable, and secure coding using best practices.</li>
          <li><strong>Launch & Support:</strong> Ongoing support, updates, and hosting to keep your site at its best.</li>
        </ul>
      </section>

      {/* CTA Section */}
      <section className="text-center py-10 bg-[#ff9b00] rounded-xl text-white">
        <h3 className="text-2xl md:text-3xl font-semibold mb-2">Ready to Build Your Website?</h3>
        <p className="mb-6 text-white/90">Let’s craft a stunning and functional website that drives business growth.</p>
        <Link
          to="/contact-us"
          className="inline-block bg-white text-[#ff9b00] font-semibold px-6 py-2 rounded-full shadow hover:bg-gray-100 transition"
        >
          Get in Touch
        </Link>
      </section>
    </div>
    </>
  );
};

export default WebsiteDesignPage;
